import React from "react";
import { useTranslation } from "react-i18next";
import { useListsRetrieval, useProjectRemoval } from "@planzone/view-models";
import { List, listsRetrievalFilter } from "@planzone/dto";
import { Modal } from "@common/modal";
import { Button } from "@common/button";
import { Props } from "./types";
import styles from "./styles.scss";

function countElement(lists: List[]): number {
  return lists.reduce(
    (total, { tasks, milestones, events, subLists }) =>
      total +
      tasks.length +
      milestones.length +
      events.length +
      countElement(subLists),
    0,
  );
}

function ProjectRemoval({ project, cancel }: Props) {
  const { t } = useTranslation();
  const filter = { ...listsRetrievalFilter, limit: 999 };
  const { isRequestPending, removeProject } = useProjectRemoval(project);
  const {
    lists,
    canRetrieveNextLists,
    isRequestPending: isListsRetrievalRequestPending,
    retrieveLists,
  } = useListsRetrieval(project, filter);
  const count = countElement([lists]);

  React.useEffect(() => {
    canRetrieveNextLists && retrieveLists({ filter, project });
  }, [canRetrieveNextLists]);

  return (
    <Modal
      header={
        !project.isTemplate
          ? t("removeProject.title")
          : t("removeProject.projectModelTitle")
      }
      headerClassName={styles.header}
      close={cancel}
    >
      <div className={styles.projectRemoval}>
        <div className={styles.main}>
          {count === 0 ? (
            <p className={styles.group}>
              <span className={styles.text}>
                {!project.isTemplate
                  ? t("removeProject.noElements")
                  : t("removeProject.projectModelNoElements")}
              </span>
            </p>
          ) : (
            <p className={styles.group}>
              <span className={styles.text}>
                {count > 1
                  ? !project.isTemplate
                    ? t("removeProject.group")
                    : t("removeProject.projectModelGroup")
                  : !project.isTemplate
                    ? t("removeProject.singleGroup")
                    : t("removeProject.projectModelSingleGroup")}
              </span>
              {count > 1 ? (
                <span className={styles.count}>
                  {" "}
                  {count} {t("removeProject.elements")}{" "}
                </span>
              ) : (
                <span className={styles.count}>
                  {" "}
                  {t("removeProject.one")} {t("removeProject.element")}{" "}
                </span>
              )}
              <span className={styles.text}>{t("removeProject.removal")}</span>
            </p>
          )}
          <div className={styles.buttons}>
            <Button type="cancel" press={cancel}>
              {t("removeProject.cancel")}
            </Button>
            <Button
              type="delete"
              status={isRequestPending ? "pending" : "none"}
              press={() => removeProject(project)}
            >
              {t("removeProject.confirm")}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

export { ProjectRemoval };
